import { forwardRef, type ButtonHTMLAttributes, type HTMLAttributes, type InputHTMLAttributes, type KeyboardEvent, type ReactNode } from 'react'
import { Icon } from './Icon'
import { useBadgePulse } from './motion'

/*
 * React wrappers of the Strevix class layer (design/system/10-code.md "the class names are the
 * contract"; strevix-components.css). Each renders the markup of its system card and nothing
 * else: no texts of their own (callers pass them, through i18n.ts where the screen has it).
 * Cards: StateSystem, Buttons, ToggleAndSegmented, Fields.
 */

export type State = 'live' | 'start' | 'warn' | 'fail' | 'idle' | 'disabled'

const cx = (...c: (string | false | undefined | null)[]) => c.filter(Boolean).join(' ')

/**
 * State badge: border style + icon + word (02-color "state is never colour alone").
 * `main` marks the one ON AIR badge of the view: it pulses only when no animated logo is in view
 * (motion.tsx). Starting always turns; LIVE chips inside panels never pulse.
 */
export function Chip({ state, size, main, still, className, children, ...rest }: { state: State; size?: 'sm' | 'lg'; main?: boolean; /** a start state that is not connecting (a ramp over minutes): the ring does not turn (07 «no loop on what is not live signal») */ still?: boolean; className?: string; children: ReactNode } & Omit<HTMLAttributes<HTMLSpanElement>, 'children'>) {
  const pulse = useBadgePulse()
  const motion = state === 'start' ? (still ? undefined : 'sx-turn') : state === 'live' && main && pulse ? 'sx-pulse' : undefined
  return (
    <span className={cx('sx-chip', state, size, className)} {...rest}>
      <Icon name={`state-${state}`} size={12} className={motion} />
      {children}
    </span>
  )
}

type ButtonVariant = 'default' | 'primary' | 'danger' | 'ghost' | 'accent-text'

/** One primary button per view (11 «one primary action»). `busy` shows the spinner and sets aria-busy. */
export const Button = forwardRef<HTMLButtonElement, { variant?: ButtonVariant; size?: 'sm' | 'lg'; icon?: boolean; busy?: boolean } & ButtonHTMLAttributes<HTMLButtonElement>>(function Button(
  { variant = 'default', size, icon, busy, className, children, type = 'button', ...rest },
  ref,
) {
  if (icon && !rest['aria-label']) throw new Error('an icon-only Button needs aria-label')
  return (
    <button ref={ref} type={type} className={cx('sx-btn', variant !== 'default' && variant, size, icon && 'icon', className)} aria-busy={busy || undefined} {...rest}>
      {busy && <span className="sx-spin" />}
      {children}
    </button>
  )
})

/** On / off switch. `pending` = the change is on its way (start colour, knob in the middle). */
export function Toggle({ checked, pending, size, onChange, className, ...rest }: { checked: boolean; pending?: boolean; size?: 'sm'; onChange: (on: boolean) => void } & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'>) {
  return (
    <button
      type="button"
      role="switch"
      className={cx('sx-toggle', size, className)}
      aria-checked={checked}
      aria-busy={pending || undefined}
      data-pending={pending ? 'true' : undefined}
      onClick={() => onChange(!checked)}
      {...rest}
    />
  )
}

/** Segmented choice, a radiogroup: arrows move the choice, the chosen segment has the accent edge. */
export function Segmented<T extends string>({ value, options, onChange, label, disabled = [], block, className }: {
  value: T
  options: { value: T; label: ReactNode; testId?: string; className?: string }[]
  onChange: (v: T) => void
  label?: string
  /** options that cannot be chosen now: struck through, skipped by the arrows */
  disabled?: T[]
  /** stretch over the width, equal segments (forms) */
  block?: boolean
  className?: string
}) {
  const live = options.filter((o) => !disabled.includes(o.value))
  const pick = (v: T) => !disabled.includes(v) && onChange(v)
  const key = (e: KeyboardEvent<HTMLSpanElement>, v: T) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault()
      pick(v)
      return
    }
    const d = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0
    if (!d || !live.length) return
    e.preventDefault()
    const at = live.findIndex((o) => o.value === v)
    const next = live[(at + d + live.length) % live.length]!
    onChange(next.value)
    ;(e.currentTarget.parentElement?.children[options.indexOf(next)] as HTMLElement | undefined)?.focus()
  }
  const focusable = options.some((o) => o.value === value) ? value : live[0]?.value
  return (
    <span
      className={cx('sx-seg', block && 'grid w-full', className)}
      style={block ? { gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` } : undefined}
      role="radiogroup"
      aria-label={label}
    >
      {options.map((o) => {
        const off = disabled.includes(o.value)
        return (
          <span
            key={o.value}
            role="radio"
            data-testid={o.testId}
            aria-checked={o.value === value}
            aria-disabled={off || undefined}
            tabIndex={o.value === focusable && !off ? 0 : -1}
            className={cx(o.value === value && 'on', block && 'justify-center text-center leading-tight', off ? 'cursor-not-allowed text-slot line-through' : 'cursor-pointer', o.className)}
            onClick={() => pick(o.value)}
            onKeyDown={(e) => key(e, o.value)}
          >
            {o.label}
          </span>
        )
      })}
    </span>
  )
}

/** Labelled field: label above, help or error below; the error has the fail icon and a word. */
export function Field({ id, label, help, error, children, className }: { id: string; label: ReactNode; help?: ReactNode; error?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={cx('sx-field', className)} data-state={error ? 'fail' : undefined}>
      <label htmlFor={id}>{label}</label>
      {children}
      {error ? (
        <span className="err" id={`${id}-err`}>
          <Icon name="state-fail" size={12} />
          {error}
        </span>
      ) : (
        help && <span className="help" id={`${id}-help`}>{help}</span>
      )}
    </div>
  )
}

/** Text input of a Field: mono by default (keys, addresses), `sans` for names and search. */
export function Input({ sans, className, ...rest }: { sans?: boolean } & InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cx('sx-input', sans && 'sans', className)} {...rest} />
}
